import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import VisaEligibilityChecker from './components/VisaEligibilityChecker';
import FinancingOptions from './components/FinancingOptions';
import LegalProcessGuide from './components/LegalProcessGuide';
import CurrencyConverter from './components/CurrencyConverter';
import DocumentChecklist from './components/DocumentChecklist';
import ResidencyBenefits from './components/ResidencyBenefits';
import ExpatCostCalculator from './components/ExpatCostCalculator';

const ExpatBuyerGuidance = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('visa');

  const sections = [
    { id: 'visa', label: 'Visa Eligibility', icon: 'CreditCard' },
    { id: 'financing', label: 'Financing Options', icon: 'Landmark' },
    { id: 'legal', label: 'Legal Process', icon: 'FileText' },
    { id: 'currency', label: 'Currency Tools', icon: 'RefreshCw' },
    { id: 'residency', label: 'Residency Benefits', icon: 'Award' },
    { id: 'documents', label: 'Document Checklist', icon: 'ClipboardList' },
    { id: 'costs', label: 'Cost Calculator', icon: 'Wallet' },
  ];

  const stats = [
    { value: 'AED 750K', label: 'Min Investor Visa', icon: 'Home' },
    { value: 'AED 2M', label: 'Golden Visa Threshold', icon: 'Star' },
    { value: '25–30%', label: 'Expat Down Payment', icon: 'Percent' },
    { value: '4%', label: 'DLD Transfer Fee', icon: 'FileText' },
  ];

  const professionalServices = [
    { name: 'Expat Property Lawyers', desc: 'Specialized in international buyer transactions, POA, and title deed transfers', icon: 'Scale', tag: 'Legal' },
    { name: 'Expat Mortgage Brokers', desc: 'Access to 20+ UAE banks with expat-friendly mortgage products', icon: 'Landmark', tag: 'Finance' },
    { name: 'RERA-Registered Agents', desc: 'Licensed agents experienced in guiding international buyers through UAE property laws', icon: 'Shield', tag: 'Real Estate' },
    { name: 'Currency Specialists', desc: 'FX brokers offering competitive rates and hedging strategies for property payments', icon: 'RefreshCw', tag: 'FX' },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #020B18 0%, #041428 50%, #020B18 100%)' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 px-4 py-3" style={{ background: 'rgba(2, 11, 24, 0.95)', borderBottom: '1px solid rgba(0, 212, 255, 0.15)', backdropFilter: 'blur(12px)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 transition-opacity hover:opacity-80">
            <Icon name="ArrowLeft" size={18} color="#00D4FF" />
            <span className="text-sm" style={{ color: '#00D4FF' }}>Back to Home</span>
          </button>
          <div className="flex items-center gap-2">
            <span className="text-lg">🌍</span>
            <span className="font-bold text-sm md:text-base" style={{ color: '#E8F4FF', fontFamily: "'Orbitron', sans-serif" }}>Expat Buyer Guidance</span>
          </div>
          <button
            onClick={() => navigate('/search-results?location=Dubai&buyer=expat')}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200"
            style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' }}
          >
            Search Properties
          </button>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        {/* Hero */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(0, 212, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.3)', color: '#00D4FF' }}>
            <span>🇦🇪</span>
            <span>UAE EXPAT PROPERTY GUIDE</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            <span style={{ color: '#00D4FF', textShadow: '0 0 30px rgba(0, 212, 255, 0.4)' }}>Expat Buyer</span>
            <span style={{ color: '#E8F4FF' }}> Guidance</span>
          </h1>
          <p className="text-base md:text-lg max-w-3xl mx-auto" style={{ color: '#6B9EC4' }}>
            Everything international buyers need to navigate UAE property acquisition — from visa requirements and financing to legal procedures and residency benefits.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {stats?.map((stat, idx) => (
            <div key={idx} className="text-center p-4 md:p-5 rounded-xl" style={{ background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
              <Icon name={stat?.icon} size={20} color="#00D4FF" />
              <div className="text-xl md:text-2xl font-bold mt-2 mb-1" style={{ color: '#00D4FF', fontFamily: "'JetBrains Mono', monospace" }}>{stat?.value}</div>
              <div className="text-xs" style={{ color: '#6B9EC4' }}>{stat?.label}</div>
            </div>
          ))}
        </div>

        {/* Section Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {sections?.map((sec) => (
            <button
              key={sec?.id}
              onClick={() => setActiveSection(sec?.id)}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
              style={activeSection === sec?.id
                ? { background: 'rgba(0, 212, 255, 0.2)', border: '1px solid rgba(0, 212, 255, 0.6)', color: '#00D4FF' }
                : { background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(0, 212, 255, 0.15)', color: '#6B9EC4' }
              }
            >
              <Icon name={sec?.icon} size={14} color={activeSection === sec?.id ? '#00D4FF' : '#6B9EC4'} />
              <span>{sec?.label}</span>
            </button>
          ))}
        </div>

        {/* Section Content */}
        <div className="mb-10">
          {activeSection === 'visa' && <VisaEligibilityChecker />}
          {activeSection === 'financing' && <FinancingOptions />}
          {activeSection === 'legal' && <LegalProcessGuide />}
          {activeSection === 'currency' && <CurrencyConverter />}
          {activeSection === 'residency' && <ResidencyBenefits />}
          {activeSection === 'documents' && <DocumentChecklist />}
          {activeSection === 'costs' && <ExpatCostCalculator />}
        </div>

        {/* Freehold Zones */}
        <div className="mb-10 p-6 md:p-8 rounded-2xl" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
          <div className="flex items-center gap-3 mb-6">
            <Icon name="MapPin" size={20} color="#00D4FF" />
            <h2 className="text-xl font-bold" style={{ color: '#E8F4FF' }}>Freehold vs Leasehold Zones</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-3 h-3 rounded-full" style={{ background: '#00FF88' }} />
                <h3 className="font-semibold" style={{ color: '#00FF88' }}>Freehold Zones (Expats Can Own)</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {['Downtown Dubai', 'Dubai Marina', 'Palm Jumeirah', 'Business Bay', 'JVC', 'JBR', 'Dubai Hills', 'Arabian Ranches', 'Yas Island (AD)', 'Saadiyat Island (AD)', 'Al Reem Island (AD)', 'Masdar City (AD)']?.map((zone, i) => (
                  <div key={i} className="flex items-center gap-1.5 p-2 rounded-lg" style={{ background: 'rgba(0, 255, 136, 0.05)', border: '1px solid rgba(0, 255, 136, 0.15)' }}>
                    <Icon name="Check" size={12} color="#00FF88" />
                    <span className="text-xs" style={{ color: '#6B9EC4' }}>{zone}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-3 h-3 rounded-full" style={{ background: '#FF6464' }} />
                <h3 className="font-semibold" style={{ color: '#FF6464' }}>Leasehold Zones (99-Year Lease)</h3>
              </div>
              <div className="space-y-2">
                {[
                  { zone: 'Deira & Bur Dubai', note: 'Older areas, leasehold only for expats' },
                  { zone: 'Sharjah (most areas)', note: 'Limited freehold zones available' },
                  { zone: 'Some Abu Dhabi areas', note: 'Outside designated investment zones' },
                ]?.map((item, i) => (
                  <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(255, 100, 100, 0.05)', border: '1px solid rgba(255, 100, 100, 0.15)' }}>
                    <div className="font-medium text-sm" style={{ color: '#E8F4FF' }}>{item?.zone}</div>
                    <div className="text-xs" style={{ color: '#6B9EC4' }}>{item?.note}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(255, 200, 0, 0.06)', border: '1px solid rgba(255, 200, 0, 0.2)' }}>
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={14} color="#FFC800" />
                  <p className="text-xs" style={{ color: '#A89060' }}>Leasehold properties offer 99-year renewable leases. Expats cannot hold freehold title in these zones.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Services */}
        <div className="mb-10">
          <h2 className="text-xl font-bold mb-6" style={{ color: '#E8F4FF' }}>Professional Services for Expats</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {professionalServices?.map((service, idx) => (
              <button
                key={idx}
                onClick={() => navigate('/professional-marketplace')}
                className="p-5 rounded-xl text-left transition-all duration-200 w-full"
                style={{ background: 'rgba(10, 22, 40, 0.8)', border: '1px solid rgba(0, 212, 255, 0.15)' }}
                onMouseEnter={e => { e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.4)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.border = '1px solid rgba(0, 212, 255, 0.15)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0, 212, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                    <Icon name={service?.icon} size={18} color="#00D4FF" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-sm" style={{ color: '#E8F4FF' }}>{service?.name}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0, 212, 255, 0.1)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.2)' }}>{service?.tag}</span>
                    </div>
                    <p className="text-xs" style={{ color: '#6B9EC4' }}>{service?.desc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center p-8 rounded-2xl" style={{ background: 'rgba(10, 22, 40, 0.9)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
          <h2 className="text-2xl font-bold mb-3" style={{ color: '#E8F4FF' }}>Ready to Buy in UAE?</h2>
          <p className="text-sm mb-6" style={{ color: '#6B9EC4' }}>Search expat-friendly properties across all 7 emirates with full RERA compliance data</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => navigate('/search-results?location=Dubai&buyer=expat')}
              className="px-8 py-3 rounded-xl font-semibold text-sm transition-all duration-200"
              style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000', boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)' }}
            >
              Search UAE Properties →
            </button>
            <button
              onClick={() => navigate('/rera-compliance-intelligence')}
              className="px-8 py-3 rounded-xl font-semibold text-sm transition-all duration-200"
              style={{ background: 'transparent', border: '2px solid rgba(0, 212, 255, 0.5)', color: '#00D4FF' }}
            >
              Check RERA Compliance →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpatBuyerGuidance;